'use client';

import {
  FloatingFocusManager,
  type FloatingFocusManagerProps,
  useMergeRefs,
} from '@floating-ui/react';
import { forwardRef, useContext, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { DatePickerFloatingContext } from './context/date-picker-floating.context';
import { DatePickerRootContext } from './context/date-picker-root.context';
import {
  datePickerContentPositionerRecipe,
  datePickerContentRecipe,
} from './styles/date-picker-content.styles';
import type { DatePickerContentPropsInterface } from './types/date-picker-content.types';

/**
 * @since 10.0.0
 */
export const DatePickerContent = forwardRef<HTMLDivElement, DatePickerContentPropsInterface>(
  (props: DatePickerContentPropsInterface, forwardedRef) => {
    const { datePicker, modal } = useContext(DatePickerRootContext);
    const floating = useContext(DatePickerFloatingContext);
    const {
      children,
      className,
      css: cssProp,
      focusManagerProps: userFocusManagerProps = {},
      style = {},
      ...rest
    } = props;

    const isModal = Boolean(modal);
    const [focusEnteredContent, setFocusEnteredContent] = useState(false);

    /*
     * The portal stays mounted through the close transition (`isMounted`), so this flag would
     * otherwise survive a fast reopen and leave `order` as `['content']` — stealing focus into
     * the calendar on the next pointer open. Reset during render so FloatingFocusManager sees
     * the cleared value on the same open cycle.
     */
    if (!datePicker.open && focusEnteredContent) {
      setFocusEnteredContent(false);
    }

    /*
     * Focus is trapped in both presentations. The mWeb breakpoint renders a true modal dialog, so
     * focus moves into it on open. Everywhere else the picker is a popover, so focus stays on the
     * trigger and the user advances into the calendar with Tab, matching the `Dropdown` and `Menu`
     * disclosure pattern. `useClick` reports reason 'click' for pointer and keyboard (Enter/Space)
     * alike, so this covers both.
     */
    const shouldFocusTrigger = !isModal && datePicker.openedByPointerRef.current;

    /*
     * Leading the order with the reference keeps the trigger out of the trap's `aria-hidden`
     * sweep, so Tab moves from the trigger to the first calendar control instead of the trap's
     * leading guard, which would wrap to the last tabbable. Dropping the reference once focus is
     * inside closes the loop around the calendar. Escape is the way out.
     */
    const focusManagerProps: Partial<FloatingFocusManagerProps> = {
      order: shouldFocusTrigger && !focusEnteredContent ? ['reference', 'content'] : ['content'],
      modal: true,
      visuallyHiddenDismiss: 'Dismiss date picker',
      ...userFocusManagerProps,
    };

    const mergedRefs = useMergeRefs([datePicker.refs.setFloating, forwardedRef]);

    const contentPositionerStyles = css(datePickerContentPositionerRecipe.raw({ modal }));
    const contentStyles = css(
      datePickerContentRecipe.raw({
        modal,
        open: datePicker.transitionStatus === 'open',
      }),
      cssProp,
    );

    return (
      <FloatingFocusManager context={floating.context} {...focusManagerProps}>
        <div
          ref={mergedRefs}
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="DatePicker.ContentPositioner"
          className={cx('c11n', 'c11n_date-picker__content-positioner', contentPositionerStyles)}
          style={
            modal
              ? {}
              : {
                  ...datePicker.floatingStyles,
                  visibility: datePicker.middlewareData.hide?.referenceHidden
                    ? 'hidden'
                    : 'visible',
                }
          }
          {...floating.getFloatingProps(rest)}
          aria-modal="true"
          aria-labelledby={datePicker.labelId}
          id={datePicker.contentId}
        >
          <div
            data-c11n-version={__C11N_VERSION__}
            data-c11n-component="DatePicker.Content"
            className={cx('c11n', 'c11n_date-picker__content', contentStyles, className)}
            style={style}
            onFocus={() => setFocusEnteredContent(true)}
          >
            {children}
          </div>
        </div>
      </FloatingFocusManager>
    );
  },
);

DatePickerContent.displayName = 'DatePicker.Content';
